<template>
  <div class="employee-lock-list">
    <div class="toolbar">
      <el-form :inline="true" size="small">
        <el-form-item label="员工姓名"><el-input v-model.trim="name" clearable placeholder="请输入员工姓名" style="width: 200px" @keyup.enter.native="search" /></el-form-item>
        <el-form-item label="身份证号"><el-input v-model.trim="idcard" clearable placeholder="请输入身份证号" style="width: 230px" @keyup.enter.native="search" /></el-form-item>
        <el-form-item label="保险公司">
          <el-select
            v-model="insuranceType"
            :disabled="isPiccRole"
            style="width: 230px"
          >
            <el-option
              v-for="item in insuranceCompanies"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="search">
            搜索
          </el-button>
          <el-button icon="el-icon-refresh" @click="reset">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <el-alert
      class="list-tip"
      type="info"
      :closable="false"
      show-icon
      :title="listTip"
    />

    <el-table
      :data="rows"
      border
      stripe
      v-loading="loading"
      height="calc(100vh - 300px)"
    >
      <el-table-column type="index" label="序号" width="65" align="center" />
      <el-table-column
        prop="name"
        label="员工姓名"
        min-width="120"
        show-overflow-tooltip
      />
      <el-table-column
        prop="idcard"
        label="身份证号"
        min-width="190"
        show-overflow-tooltip
      />
      <el-table-column
        prop="policy_no"
        label="保单号"
        min-width="190"
        show-overflow-tooltip
      />
      <el-table-column label="员工状态" width="100" align="center">
        <template slot-scope="scope">
          <el-tag :type="statusType(scope.row.status)" size="small">
            {{ statusText(scope.row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column
        prop="note"
        label="锁定描述"
        min-width="220"
        show-overflow-tooltip
      >
        <template slot-scope="scope">{{ scope.row.note || "-" }}</template>
      </el-table-column>
      <el-table-column label="生效时间" width="165">
        <template slot-scope="scope">{{ date(scope.row.effect_date) }}</template>
      </el-table-column>
      <el-table-column label="失效时间" width="165">
        <template slot-scope="scope">{{ date(scope.row.expire_date) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="160" align="center">
        <template slot-scope="scope">
          <el-button
            v-if="canLock(scope.row)"
            type="text"
            @click="openLock(scope.row)"
          >
            出险锁定
          </el-button>
          <template v-else-if="Number(scope.row.status) === 8">
            <span class="locked-text">已锁定</span>
            <el-button type="text" @click="unlock(scope.row)">解锁</el-button>
          </template>
          <span v-else class="unavailable-text">不可操作</span>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      class="pagination"
      :current-page="page"
      :page-size="pageSize"
      :page-sizes="[10, 15, 20, 25]"
      :total="total"
      layout="total, sizes, prev, pager, next, jumper"
      @size-change="changeSize"
      @current-change="changePage"
    />

    <employee-lock-dialog
      :visible.sync="dialogVisible"
      :employee="currentEmployee"
      @success="fetchData"
    />
  </div>
</template>

<script>
import dayjs from "dayjs";
import { getToken } from "@/lib/local";
import {
  getEmployerEmployeeLockList,
  unlockEmployerEmployee
} from "@/api/employer-insurance";
import EmployeeLockDialog from "./components/EmployeeLockDialog.vue";

const INSURANCE_COMPANIES = [
  { value: 2, label: "大地财产保险有限公司" },
  { value: 16, label: "信达财产保险股份有限公司" },
  { value: 128, label: "中国人民保险集团股份有限公司" }
];

export default {
  name: "EmployeeInsuranceLock",
  components: { EmployeeLockDialog },
  data() {
    return {
      loading: false,
      name: "",
      idcard: "",
      insuranceType: "",
      lockedOnly: true,
      rows: [],
      page: 1,
      pageSize: 15,
      total: 0,
      dialogVisible: false,
      currentEmployee: null,
      insuranceCompanies: INSURANCE_COMPANIES
    };
  },
  computed: {
    roleId() {
      return Number(this.$store.state.user.type || getToken("admin_role") || 0);
    },
    isPiccRole() {
      return this.roleId === 10;
    },
    listTip() {
      return this.lockedOnly
        ? "当前显示已锁定员工；如需锁定其他员工，请按员工姓名、身份证号或保险公司搜索。"
        : "当前显示搜索结果，可对已生效或未生效的员工执行出险锁定。";
    }
  },
  created() {
    if (this.isPiccRole) this.insuranceType = 128;
    this.fetchData();
  },
  methods: {
    buildParams() {
      const params = {
        page: this.page,
        rows: this.pageSize
      };

      if (this.lockedOnly) params.status = 8;
      if (this.isPiccRole) params.insurance_type = 128;

      if (!this.lockedOnly) {
        if (this.name) params.name = this.name;
        if (this.idcard) params.idcard = this.idcard;
        if (this.insuranceType !== "") params.insurance_type = this.isPiccRole ? 128 : this.insuranceType;
      }
      return params;
    },
    async fetchData() {
      this.loading = true;
      try {
        const res = await getEmployerEmployeeLockList(this.buildParams());
        this.rows = (res && res.rows) || [];
        this.total = Number((res && res.total) || this.rows.length);
      } finally {
        this.loading = false;
      }
    },
    search() {
      if (!this.name && !this.idcard && this.insuranceType === "") {
        this.$message.warning("请至少输入一个搜索条件");
        return;
      }
      this.lockedOnly = false;
      this.page = 1;
      this.fetchData();
    },
    reset() {
      this.name = "";
      this.idcard = "";
      this.insuranceType = this.isPiccRole ? 128 : "";
      this.lockedOnly = true;
      this.page = 1;
      this.fetchData();
    },
    changeSize(value) {
      this.pageSize = value;
      this.page = 1;
      this.fetchData();
    },
    changePage(value) {
      this.page = value;
      this.fetchData();
    },
    canLock(row) {
      return [1, 2].indexOf(Number(row.status)) !== -1;
    },
    openLock(row) {
      this.currentEmployee = row;
      this.dialogVisible = true;
    },
    async unlock(row) {
      await this.$confirm(
        `确定解除“${row.name || "该员工"}”的出险锁定吗？`,
        "解锁确认",
        { type: "warning" }
      );
      const res = await unlockEmployerEmployee({ id: Number(row.id) });
      if (!res) return;
      this.$message.success("解锁成功");
      this.fetchData();
    },
    date(value) {
      return value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "-";
    },
    statusText(value) {
      return {
        1: "已生效",
        2: "未生效",
        4: "已删除",
        8: "已锁定"
      }[Number(value)] || "未知";
    },
    statusType(value) {
      return {
        1: "success",
        2: "warning",
        4: "info",
        8: "danger"
      }[Number(value)] || "info";
    }
  }
};
</script>

<style lang="less" scoped>
.toolbar {
  margin-bottom: 12px;
}
.list-tip {
  margin-bottom: 12px;
}
.pagination {
  margin-top: 16px;
  text-align: right;
}
.locked-text {
  margin-right: 10px;
  color: #909399;
}
.unavailable-text {
  color: #c0c4cc;
}
</style>
